// Import Types
import type { QueryConfigurations } from 'types';
// Import Enums
import { SegmentationCategoryTypeEnum } from 'enums';
// Import Services
import {
    buildSchemaColorMap,
    buildSchemaDependencies,
    collectSegmentationTreeSchemaIds,
} from 'services/segment/schemaDependencies';
// Import Constants
import { ClientKPIColumnTypes, JSON_OBJECT } from 'constants/common';
import { objectTypesMap, hashTypeMap } from '../constants';
// Import UI Containers
import {
    UIHashGroupContainer,
    UIHashButtonItem,
    UIHashComparisonContainer,
    UIComparisonObject,
} from '../UIHashContainers';

/// hash data
export function hashRecursion(treeNode, storageHash, hashKey, parentHashKey, schemaColorMap = {}) {
    const categoryList = treeNode.SubCategories || [];
    for (let i = 0; i < categoryList.length; i++) {
        const hashKeyIndex = `${objectTypesMap.categoriesFilters}${categoryList[i].SegmentationCategoryId}`;
        hashRecursion(categoryList[i], storageHash, hashKeyIndex, hashKey, schemaColorMap);
    }

    let uiColor;
    const uiType = treeNode.Type;

    if (treeNode.SegmentationCategoryUI) {
        //should be in every
        uiColor = treeNode.SegmentationCategoryUI.Color;
    }
    const { DisplayName } = treeNode;
    const groupContainer = new UIHashGroupContainer();
    groupContainer.parent = new UIHashButtonItem(parentHashKey, null, DisplayName, null, uiColor, null, null, uiType);
    storageHash[hashKey] = groupContainer;
    categoriesToHash(categoryList, storageHash, hashKey);
    filtersToHash(treeNode.SegmentationFilters, storageHash, hashKey, uiColor, uiType, schemaColorMap);
}

export function comparisonToHash(comparison, storageHash, hashKey, parentHashKey, options) {
    const { Color, DisplayName, Comparision, Type, Source, ViewType, Validation, SegmentationFilterId } = comparison;
    const validationOptions = JSON.parse(Validation || JSON_OBJECT);
    const { MinValue, Pattern, MaxValue, Name } = validationOptions;
    const {
        filterColumnId,
        filterDisplayName,
        applicationColumnId,
        schemaId,
        schemaName,
        columnType,
        isAggregated,
        isKPI,
        uiColor,
        uiType,
        schemaDeps = [],
    } = options;
    const comparisonContainer = new UIHashComparisonContainer();
    const containerFilterDisplayName = `${filterDisplayName}(${DisplayName})`;
    comparisonContainer.parent = new UIHashButtonItem(
        parentHashKey,
        null,
        containerFilterDisplayName,
        null,
        uiColor,
        null,
        filterDisplayName,
        uiType,
    );
    comparisonContainer.comparison = new UIComparisonObject(
        Comparision,
        DisplayName,
        ViewType,
        Type,
        Color,
        Source,
        Pattern,
        MinValue,
        MaxValue,
        filterColumnId,
        SegmentationFilterId,
        hashKey,
        applicationColumnId,
        schemaId,
        schemaName,
        columnType,
        isAggregated,
        isKPI,
        Name,
        uiType,
    );
    comparisonContainer.comparison.schemaDeps = schemaDeps;
    storageHash[hashKey] = comparisonContainer;
}

export function categoriesToHash(categoryList, storageHash, hashKey) {
    let uiColor;
    let nextBtn;
    let nextHashKey;
    for (let i = 0; i < categoryList.length; i++) {
        const uiType = categoryList[i].Type;
        if (categoryList[i].SegmentationCategoryUI) {
            uiColor = categoryList[i].SegmentationCategoryUI.Color;
        }
        nextHashKey = `${objectTypesMap.categoriesFilters}${categoryList[i].SegmentationCategoryId}`;
        nextBtn = new UIHashButtonItem(
            nextHashKey,
            hashTypeMap.category,
            categoryList[i].DisplayName,
            null,
            uiColor,
            null,
            null,
            uiType,
        );
        storageHash[hashKey].addNextItem(nextBtn);
    }
}

export function isFilterAggregated(columnType) {
    return (columnType & (ClientKPIColumnTypes.Aggregate | ClientKPIColumnTypes.Formula)) > 0;
}

export function filtersToHash(filterList, storageHash, hashKey, uiColor, uiType, schemaColorMap = {}) {
    let nextBtn;
    let nextHashKey;
    for (let i = 0; i < filterList.length; i++) {
        const columnIdIndex = filterList[i].ColumnId;
        const schemaDeps = buildSchemaDependencies(filterList[i].Column, schemaColorMap);
        if (filterList[i].ComparisionUIs) {
            const hashKeyForComparison = `${objectTypesMap.filterComparisons}${columnIdIndex}`;
            filterList[i].ComparisionUIs.ColumnId = columnIdIndex;
            const options = {
                uiColor: uiColor,
                uiType: uiType,
                filterDisplayName: filterList[i].DisplayName,
                filterDescription: filterList[i].Description,
                filterColumnId: columnIdIndex,
                applicationColumnId: filterList[i].ApplicationColumnId,
                schemaId: filterList[i].Column.SchemaId,
                schemaName: filterList[i].Column.SchemaName,
                columnType: filterList[i].Column.Type,
                isAggregated: isFilterAggregated(filterList[i].Column.Type),
                isKPI: filterList[i].Column.IsKPI,
                schemaDeps,
            };
            comparisonsToHash(filterList[i].ComparisionUIs, storageHash, hashKeyForComparison, hashKey, options);
        }
        nextHashKey = objectTypesMap.filterComparisons + columnIdIndex;
        nextBtn = new UIHashButtonItem(
            nextHashKey,
            hashTypeMap.filter,
            filterList[i].DisplayName,
            filterList[i].Description,
            uiColor,
            null,
            null,
            uiType,
            filterList[i].SegmentationFilterId,
        );
        nextBtn.schemaDeps = schemaDeps;
        storageHash[hashKey].addNextItem(nextBtn);
    }
}

export function startHashing(dataToHash): QueryConfigurations {
    const hahStorage = {};
    const treeNodeForRecursion = {
        SubCategories: dataToHash,
        SegmentationFilters: [],
        DisplayName: '',
        Type: SegmentationCategoryTypeEnum.None,
        SegmentationCategoryUI: {
            Color: '#ff5418',
        },
    };
    const rootHashKay = objectTypesMap.categoriesFilters + 'root';
    const schemaColorMap = buildSchemaColorMap(collectSegmentationTreeSchemaIds(dataToHash));
    hashRecursion(treeNodeForRecursion, hahStorage, rootHashKay, null, schemaColorMap);
    return hahStorage;
}

export function comparisonsToHash(comparisonsList, storageHash, hashKey, parentHashKey, options) {
    let nextBtn;
    let nextHashKey;
    const {
        filterDisplayName,
        uiColor,
        uiType,
        filterColumnId,
        filterDescription,
        minValue,
        applicationColumnId,
        schemaId,
        schemaName,
        columnType,
        isAggregated,
        isKPI,
        schemaDeps = [],
    } = options;
    const groupContainer = new UIHashGroupContainer();
    const groupContainerParentBtn = new UIHashButtonItem(
        parentHashKey,
        null,
        filterDisplayName,
        filterDescription,
        uiColor,
        null,
        null,
        uiType,
    );
    groupContainerParentBtn.schemaDeps = schemaDeps;
    groupContainer.parent = groupContainerParentBtn;
    for (let i = 0; i < comparisonsList.length; i++) {
        const comparisonIndex = comparisonsList[i];
        const hashKeyForComparisonIndex = `${objectTypesMap.comparison}${filterColumnId}/${comparisonIndex.Comparision}`;
        nextHashKey = hashKeyForComparisonIndex;
        nextBtn = new UIHashButtonItem(
            nextHashKey,
            hashTypeMap.comparison,
            comparisonIndex.DisplayName,
            null,
            uiColor,
            comparisonIndex.Comparision,
            null,
            uiType,
        );
        groupContainer.addNextItem(nextBtn);
        const optionsForComparison = {
            uiColor: uiColor,
            uiType: uiType,
            filterDisplayName: filterDisplayName,
            filterDescription: filterDescription,
            filterColumnId: filterColumnId,
            minValue: minValue,
            applicationColumnId: applicationColumnId,
            schemaId: schemaId,
            schemaName: schemaName,
            columnType: columnType,
            isAggregated: isAggregated,
            isKPI: isKPI,
            schemaDeps: schemaDeps,
        };
        comparisonToHash(comparisonIndex, storageHash, hashKeyForComparisonIndex, hashKey, optionsForComparison);
    }
    storageHash[hashKey] = groupContainer;
}
